import { useEffect } from 'react'
import { useAppStore } from '@shared/store'
import { Header } from '@widgets/header'
import { Footer } from '@widgets/footer'
import { useSeo } from '@shared/lib/seo'
import { SITE } from '@shared/config'
import * as s from './LegalPage.css'

const MARKS = [
  {
    title: 'Sponsored markup',
    text: 'Affiliate links carry sponsored link attributes in the page markup, so browsers and search engines can identify the relationship.',
  },
  {
    title: 'Nearby disclosure',
    text: 'Every article that contains product links shows an affiliate disclosure notice near the recommendations, as Amazon requires.',
  },
  {
    title: 'Plain wording',
    text: 'A link may also be identified by wording such as affiliate link, paid link, or commissions earned.',
  },
]

const PRINCIPLES = [
  {
    title: 'Commissions do not set conclusions',
    text: 'Commission rates have no input into the care conclusion of an article. Recommendations are written before product references are added.',
  },
  {
    title: 'Products are picked for the use case',
    text: 'Product references are selected for the specification, label, or use case being discussed, not for how much a listing pays.',
  },
  {
    title: 'Check the listing before you buy',
    text: 'Availability, price, formulation, and product labeling can change. Check the current listing and label before buying or using a product.',
  },
]

export function AffiliateDisclosurePage() {
  useSeo({
    title: `Affiliate Disclosure | ${SITE.name}`,
    description: `How ${SITE.name} uses Amazon affiliate links, earns commissions, and keeps product recommendations separate from commission rates.`,
    path: '/affiliate-disclosure/',
    type: 'website',
  })
  useEffect(() => { window.scrollTo(0, 0) }, [])
  const isDarkMode = useAppStore((st) => st.isDarkMode)

  return (
    <>
      <Header />
      <main className={s.page}>
        <section className={s.hero}>
          <div className={s.heroBrandRow}>
            <img src="/images/plantology_logo.png" alt="Plantology" className={s.heroLogo} style={isDarkMode ? { filter: 'invert(1)' } : undefined} />
            <span className={s.heroBrandDivider} aria-hidden="true" />
            <img src="/images/logo_black.webp" alt={SITE.name} className={s.heroSiteLogo} style={isDarkMode ? { filter: 'invert(1)' } : undefined} />
          </div>
          <p className={s.heroLabel}>Disclosure</p>
          <h1 className={s.heroTitle}>Affiliate Disclosure</h1>
          <p className={s.heroDesc}>
            Some articles link to products on Amazon. This page covers how those
            links earn commissions and how they are marked.
          </p>
        </section>

        <div className={s.statementCard}>
          <p className={s.statementQuote}>
            As an Amazon Associate I earn from qualifying purchases.
          </p>
          <p className={s.statementText}>
            Some {SITE.name} articles contain links to products on Amazon. If you follow
            one of these links and complete a qualifying purchase, {SITE.name} may receive
            commission income at no extra cost to you. Those commissions fund hosting and
            operations. Participation in the Amazon Associates Program does not mean that
            Amazon sponsors or endorses {SITE.name}.
          </p>
        </div>

        <section className={s.section}>
          <p className={s.sectionLabel}>Transparency</p>
          <h2 className={s.sectionTitle}>How affiliate links are marked</h2>
          <p className={s.sectionBody}>
            Amazon requires Associates to make the relationship clear both near affiliate
            recommendations and on the site. Links on {SITE.name} are marked three ways.
          </p>
          <div className={s.cardGrid}>
            {MARKS.map((m) => (
              <div key={m.title} className={s.card}>
                <h3 className={s.cardTitle}>{m.title}</h3>
                <p className={s.cardText}>{m.text}</p>
              </div>
            ))}
          </div>
        </section>

        <div className={s.shimmer} />

        <section className={s.section}>
          <p className={s.sectionLabel}>Independence</p>
          <h2 className={s.sectionTitle}>Editorial independence</h2>
          <div className={s.principleList}>
            {PRINCIPLES.map((p, i) => (
              <div key={p.title} className={s.principleItem}>
                <span className={s.principleNum}>{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <h3 className={s.principleTitle}>{p.title}</h3>
                  <p className={s.principleText}>{p.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className={s.linkRow}>
          <a className={s.linkChip} href="/privacy-policy/">Privacy Policy</a>
          <a className={s.linkChip} href="/about/">Editorial policy</a>
          <a
            className={s.linkChip}
            href="https://affiliate-program.amazon.com/help/node/topic/GHQNZAU6669EZS98"
            target="_blank"
            rel="nofollow noopener"
          >
            Amazon disclosure guidance ↗
          </a>
        </div>
      </main>
      <Footer />
    </>
  )
}
